import { Box } from '@mui/material';
// InactiveTooltip.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DsIcon } from '../../icons/DsIcon';
import { TourStep } from '../types';
import {
  CardinalOrientation,
  OrientationCoords,
  getTargetPosition as utilGetTargetPosition,
  getTooltipPosition as utilGetTooltipPosition,
} from '../utils/positioning';
import { getCornerStyles } from '../utils/tipCorner';
import { isElementInView as utilIsElementInView } from '../utils/viewport';

const getTargetPosition = utilGetTargetPosition as any;
const getTooltipPosition = utilGetTooltipPosition as any;
const isElementInView = utilIsElementInView as any;

/**
 * Minimal interface for InactiveTooltip props.
 * Add/modify fields depending on how your app tracks steps.
 */
interface InactiveTooltipProps {
  step: TourStep; // data for this step, including selector, content, etc.
  isVisible?: boolean; // optional: to decide if you want to hide some tooltips
  containerRoot?: Element; // the same root you use in your main Tour, if needed
  index: number; // the step's index
  activeIndex: number; // which step is active
  goToStep: (stepIndex: number) => void;
}

export const Tip: React.FC<InactiveTooltipProps> = ({
  step,
  isVisible = true,
  containerRoot,
  index,
  activeIndex,
  goToStep,
}) => {
  const [orientationCoords, setOrientationCoords] = useState<OrientationCoords | null>(null);
  const [cornerStyles, setCornerStyles] = useState<React.CSSProperties>({});
  const tipRef = React.useRef<HTMLElement | undefined>(undefined);
  const [isInViewport, setIsInViewport] = useState(true);
  // Attempt to find this step's target
  const targetElement = containerRoot?.querySelector(step.selector) as HTMLElement;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setCornerStyles(getCornerStyles(orientationCoords?.orientation));
  }, [orientationCoords]);

  const updateDimensions = () => {
    if (
      !isInViewport ||
      !containerRoot ||
      !step.selector ||
      index === activeIndex ||
      !targetElement ||
      orientationCoords?.orientation === CardinalOrientation.CENTER
    ) {
      // if we have no root, no selector, or if this step is currently active, skip
      setOrientationCoords(null);
      return;
    }

    if (!targetElement) {
      setOrientationCoords(null);
      return;
    }

    // Get position from your existing utils
    const targetPos = getTargetPosition(containerRoot, targetElement);
    if (targetPos) {
      const tipPosition: OrientationCoords = getTooltipPosition({
        target: targetElement,
        tooltip: tipRef.current,
        padding: 0,
        tooltipSeparation: 0,
        orientationPreferences: step.tipOrientationPreferences,
        root: containerRoot,
        getPositionFromCandidates: undefined,
        disableAutoScroll: false,
        allowForeignTarget: false,
        selector: step?.selector,
        isPreferredCandidatesIncluded: true,
      });

      setOrientationCoords({
        orientation: tipPosition?.orientation,
        coords: {
          x: tipPosition?.coords.x,
          y: tipPosition?.coords.y, // for instance, place 10 pixels above the target
        },
      });
    }
  };

  const scheduleViewportCheck = useCallback(() => {
    if (!targetElement || !isElementInView) return;

    const startTime = Date.now();
    const duration = 500; // Check for 2 seconds
    const interval = 100; // Check every 100ms

    const poll = () => {
      if (Date.now() - startTime < duration) {
        if (isElementInView(containerRoot, targetElement)) {
          setIsInViewport(true);
        } else {
          setIsInViewport(false);
        }

        timeoutRef.current = setTimeout(poll, interval);
      }
    };

    poll();
  }, [isElementInView, targetElement]);

  useEffect(() => {
    window.addEventListener('resize', updateDimensions);
    updateDimensions();

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [containerRoot]);

  useEffect(() => {
    updateDimensions();
    scheduleViewportCheck();
  }, [containerRoot, step.selector, index, activeIndex, tipRef.current, targetElement]);

  if (
    !isInViewport ||
    !orientationCoords?.coords ||
    !isVisible ||
    index === activeIndex ||
    !targetElement ||
    orientationCoords?.orientation === CardinalOrientation.CENTER
  ) {
    return null;
  }

  return (
    <Box
      ref={tipRef}
      onClick={(e) => {
        e.stopPropagation();
        // goToStep(index);
      }}
      sx={{
        position: 'absolute',
        top: orientationCoords?.coords.y,
        left: orientationCoords?.coords.x,
        width: 38,
        height: 38,
        borderColor: 'iceGray.800',
        zIndex: 9999,
        borderTopLeftRadius: '50%',
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
        borderBottomLeftRadius: '50%',
        transformOrigin: 'center center',
        transform: 'none',
        // cursor: 'pointer',
        // Merge in the corner & transform styles
        ...cornerStyles,
        // Optional: some padding inside the tooltip
        borderWidth: '5px',
        // cursor: 'pointer',
        pointerEvents: 'all',
        bgcolor: 'iceGray.800',
      }}
    >
      <Box
        sx={{
          borderRadius: '50%',
          bgcolor: (theme) => theme.palette.primary.main,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DsIcon icon="T_TIP" size={14} sx={{ fontWeight: 900 }} color="info.contrastText" />
      </Box>
    </Box>
  );
};
